import type { Auth, User } from "firebase/auth";
import { signOut } from "./signOut";
import { RefreshFailed, type RefreshFailure, refreshFailure } from "./RefreshFailed";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import * as api from "./api";
import {
  accountCount,
  capWeeksText,
  dayText,
  fillOf,
  flagHelp,
  flagText,
  flagsOf,
  OFF_SEASON_HELP,
  galText,
  groupAccounts,
  loadParts,
  loadText,
  nearFullCount,
  netGal,
  pickupRows,
  newestText,
  pinRows,
  pinStateText,
  rateText,
  scrapText,
  STATED_RATE_HELP,
  STATUS_TEXT,
  weekGroupText,
  weeksLeftText,
  windowText,
} from "./accounts";
import { earlyPullOf, nextVisitText, projectedText, visitCellParts } from "./calendar";
import { CALLS_FIRST, CALLS_SHOWN, callDay, callProblem, callTime, REASON_TEXT, REASONS, type Reason } from "./calls";
import { ROUGH_PIN_CLASS } from "./location";
import { type AccountFilter, filterAccounts, isFiltered, NO_FILTER, pinView, readFilter, sectionsFor, writeFilter } from "./filter";
import { clockTime } from "./format";
import { go, useCrumb, usePoll, useSearchState, useTitle } from "./hooks";
import { asksNoScrap, capacityWarning, dateError, parseGallons, todayIn } from "./pickup";
import { cleanTruck, lockedBy, pickupLoadText, truckError } from "./loads";
import { planChange, planForm, undoChange, weekGroupLabel, weekGroupsFor } from "./plan";
import { blackoutText, clockText, DAY_NAMES, keptDay, MAX_BLACKOUTS, monthDay, MONTHS, offSeasonText, seasonText } from "./seasons";
import { allPicked, BULK_FIELD_TEXT, BULK_FIELDS, bulkChanges, type BulkForm, changedText, confirmPinsText, editOff, EMPTY_BULK, editText, pinIds, pinsOff, pinsDoneText, sheetText, toggled, valueText, withAll } from "./bulk";
import { IssueForm, IssueTable } from "./IssuePages";
import { callLeadPath, keepCallNote } from "./leads";
import { WorkOrderForm, WorkOrderTable } from "./WorkOrderPages";
import { recommendedCell, recommendedText } from "./workOrders";
import { isId } from "./route";
import { pickupField, plainFailure, type Saved, SERVER_PROBLEM, saveFailure } from "./save";
import { fieldFixText } from "./offline/messages";
import { SHEET_WARNING, sheetHeld } from "./sheetImport";
import { ACCOUNT_COLUMNS, CALL_COLUMNS, PICKUP_COLUMNS, REVIEW_RECORD_COLUMNS } from "./columns";
import { weekOptions, weekTabs } from "./cycle";
import { WeekTabs } from "./WeekTabs";
import { SOURCE_FILTERS, type SourceFilter } from "./dataSource";
import { SourceTag } from "./SourceTag";
import { areaOfPoint, OUTSIDE_HELP, outsideText } from "./areaMap";
import { AccountSalesSection } from "./AccountSales";
import { CommercialFact, ContractSection } from "./ContractPages";
import { DeletedBanner, DeleteRecord } from "./DeleteRecord";
import { ForecastReset } from "./ForecastReset";
import { GallonsFields, NoScrapConfirm } from "./GallonsFields";
import { LocationCard } from "./LocationCard";
import { accountPinFilters, LAYER_PARAM, MAP_PARAM } from "./mapView";
import { PinMap, useMapMode, ViewSwitch } from "./PinMap";
import { anchorText, bandText, chanceText, forecastFromText, sensorLagText, sensorText, swingText } from "./forecast";
import { deletedTitle } from "./deletion";
import { ContainerSensor } from "./ContainerSensor";
import { SensorSection } from "./TankPanel";
import { navigate } from "./Menu";
import { PayoutHistory } from "./RebatePages";
import { AccountVisits } from "./VisitsPage";
import { AccountRequests } from "./AccountRequests";
import { PortalInvite } from "./PortalInvite";
import { NEED_DAYS, openRequest, personName, shortDay } from "./requests";
import { memberName } from "./memberNames";
import { AccountPhotos } from "./PhotoPages";
import { COMMERCIAL_TEXT, COMMERCIALS } from "./contracts";
import { KIND_SHORT, RESULT_TEXT, REVIEW_KINDS, type ReviewKind, reviewedText } from "./review";
import { type Sort, sortRows } from "./sort";
import { failedText, keepAccounts, keptText, logAccountsLoad, logRevalidate, useAccounts } from "./store/accounts";
import { syncNow, useSyncStatus, wantAccounts } from "./store/sync";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { ConeAnchor } from "./ConeCard";
import { AccountConeChart } from "./ConeChart";
import { FillBar } from "./FillBar";
import { HelpText, Hint } from "./Hint";
import { accountHints, earlyPullHelp, NEXT_VISIT_HELP, statusHelp } from "./accountHints";
import { Skeleton, SkeletonBar, SkeletonTools } from "./Skeleton";

function Recommended({ account }: { account: api.Account }) {
  const cell = account.status === "OFF" ? null : recommendedCell(account.projection, account.capacity_gal);
  if (!cell) return <>–</>;
  return (
    <>
      <span className={cell.strong ? "recommend-change" : undefined}>{cell.text}</span>
      {cell.now && <span className="sub">{cell.now}</span>}
    </>
  );
}

/** Rule V-20: the pin is in the shape of another area than the plan. */
function OutsideChip({ account }: { account: api.Account }) {
  const flag = account.outside_area;
  if (!flag) return null;
  return (
    <Hint text={`${outsideText(flag)} ${OUTSIDE_HELP}`} stop={false}>
      <span className="chip-flag outside-area below" data-testid="outside-area-chip">
        Pin in {flag.pin_area}
      </span>
    </Hint>
  );
}

function Flags({ account }: { account: api.Account }) {
  return (
    <>
      {account.projection?.off_season && (
        <Hint text={OFF_SEASON_HELP} stop={false}>
          <span className="chip-flag" data-testid="off-season-chip">
            Off season
          </span>
        </Hint>
      )}
      {earlyPullOf(account) && (
        <Hint stop={false} text={earlyPullHelp(account)}>
          <span className="chip-flag serious" data-testid="early-pull-chip">
            Early pull
          </span>
        </Hint>
      )}
      {flagsOf(account).map((flag) => (
        <Hint key={flag} text={flagHelp(account, flag)} stop={false}>
          <span className={flag === "near_full" ? "chip-flag serious" : "chip-flag"} data-flag={flag}>
            {flagText(account, flag)}
          </span>
        </Hint>
      ))}
    </>
  );
}

/** Rule U-28: the hints of the row in one card when the account link has the keyboard focus, so the chips add no tab stop. */
function RowHintList({ account }: { account: api.Account }) {
  return (
    <ul className="row-hints" data-testid="row-hints">
      {accountHints(account).map((hint) => (
        <li key={hint.label}>
          <strong>{hint.label}:</strong> {hint.text}
        </li>
      ))}
    </ul>
  );
}

const ACCOUNT_SPAN = ACCOUNT_COLUMNS.length;
const WRAP_HEADS = ["load", "left", "visit", "pickup"];

function AccountRow({
  account,
  base,
  picked,
  onPick,
}: {
  account: api.Account;
  base: string;
  picked?: boolean;
  onPick?: (id: string) => void;
}) {
  const fill = fillOf(account);
  const share = fill?.share ?? null;
  const cone = fill != null || (account.status !== "OFF" && account.projection?.load_gal != null);
  const coneLabel = `${account.number} ${account.name}`;
  const coneWords = fill && !fill.estimated ? STATED_RATE_HELP : undefined;
  const visit = visitCellParts(account);
  const load = loadParts(account);
  return (
    <tr data-testid="account-row" data-account={account.number} className={picked ? "picked" : undefined}>
      <th scope="row" className="account-number">
        {onPick && (
          <label className="pick-target">
            <input
              type="checkbox"
              className="pick"
              data-testid="account-pick"
              aria-label={`Select ${account.number}`}
              checked={Boolean(picked)}
              onChange={() => onPick(account.id)}
            />
          </label>
        )}
        <Hint text={<RowHintList account={account} />} stop={false} focusOnly testId="account-row-hints">
          <a href={`${base}/accounts/${account.id}`} onClick={go} data-testid="account-link">
            {account.number}
          </a>
        </Hint>
      </th>
      <td data-label="">
        <span className="clip name" title={account.name}>
          {account.name}
        </span>
      </td>
      <td className="flags wrap" data-label="">
        <Flags account={account} />
      </td>
      <td data-label="Area">
        {account.plan?.area ?? "–"}
        {weekGroupText(account) && <span className="sub below">{weekGroupText(account)}</span>}
        <OutsideChip account={account} />
      </td>
      <td data-label="Status">
        <Hint text={statusHelp(account)} stop={false}>
          {STATUS_TEXT[account.status]}
        </Hint>
        {account.mismatch && <span className="sub below" data-testid="row-mismatch"> mismatch</span>}
      </td>
      <td data-testid="account-source" data-label="Source">
        {account.data_source ? <SourceTag tag={account.data_source} stop={false} /> : "–"}
      </td>
      <td className="num" data-testid="account-load" data-label="Load">
        <ConeAnchor accountId={cone ? account.id : null} label={coneLabel} words={coneWords} empty={!fill?.estimated} rowFocus={false} testId="account-load-cone">
          {load.text}
        </ConeAnchor>
        {load.of && <span className="sub below">{load.of}</span>}
        {account.status === "HOLD" && account.projection?.load_gal != null && <span className="sub below">on hold</span>}
      </td>
      <td className="num" data-testid="account-recommended" data-label="Recommended (estimated)">
        <Recommended account={account} />
      </td>
      <td className="num" data-label="Full" data-testid="account-full">
        {share == null ? (
          "–"
        ) : (
          <ConeAnchor accountId={cone ? account.id : null} label={coneLabel} words={coneWords} empty={!fill?.estimated} rowFocus={false} ownFocus testId="account-full-cone">
            <span className="fill-cell" data-source={fill?.estimated ? "estimate" : "rate"}>
              <FillBar pct={share * 100} coloured />
              {`${Math.round(Math.min(share, 1) * 100)}%`}
            </span>
          </ConeAnchor>
        )}
      </td>
      <td className="weeks-left num" data-label="Weeks left">
        {weeksLeftText(account)}
      </td>
      <td data-testid="account-next-visit" data-label="Next visit">
        <Hint text={visit.day === "–" ? undefined : NEXT_VISIT_HELP} stop={false}>
          {visit.day}
        </Hint>
        {visit.share && <span className="sub below">{visit.share}</span>}
      </td>
      <td data-label="Last pickup">{account.last_pickup ? dayText(account.last_pickup) : "–"}</td>
    </tr>
  );
}

function PinState({ account }: { account: api.Account }) {
  const text = pinStateText(account);
  return account.door?.check ? (
    <span className={ROUGH_PIN_CLASS} data-testid="pin-rough">
      {text}
    </span>
  ) : (
    <>{text}</>
  );
}

function PinList({
  accounts,
  base,
  picked,
  onPick,
}: {
  accounts: api.Account[];
  base: string;
  picked: ReadonlySet<string>;
  onPick?: (id: string) => void;
}) {
  return (
    <div data-testid="pin-list">
      <p className="sub">Look at each pin on the map or on the account page before you confirm it.</p>
      <div className="table-scroll" tabIndex={0} role="region" aria-label="Pins to check">
        <table className="list pin-list">
          <thead>
            <tr>
              <th scope="col">Account</th>
              <th scope="col">Name</th>
              <th scope="col">Address</th>
              <th scope="col">Area</th>
              <th scope="col">Pin</th>
            </tr>
          </thead>
          <tbody>
            {pinRows(accounts).map((account) => (
              <tr key={account.id} data-testid="pin-row" data-account={account.number} className={picked.has(account.id) ? "picked" : undefined}>
                <th scope="row" className="account-number">
                  {onPick && (
                    <label className="pick-target">
                      <input
                        type="checkbox"
                        className="pick"
                        data-testid="account-pick"
                        aria-label={`Select ${account.number}`}
                        checked={picked.has(account.id)}
                        onChange={() => onPick(account.id)}
                      />
                    </label>
                  )}
                  <a href={`${base}/accounts/${account.id}`} onClick={go} data-testid="account-link">
                    {account.number}
                  </a>
                </th>
                <td data-label="Name">{account.name}</td>
                <td data-label="Address" data-testid="pin-address">
                  {account.address || "–"}
                </td>
                <td data-label="Area">{account.plan?.area ?? "–"}</td>
                <td data-label="Pin">
                  <PinState account={account} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DeletedAccounts({ base, org, user, onRestored }: { base: string; org: api.Bootstrap; user: User; onRestored: () => void }) {
  const [rows, setRows] = useState<api.Account[] | "failed" | undefined>(undefined);
  const [status, setStatus] = useState<Saved | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then((token) => api.accountList(token, true))
      .then((found) => live && setRows(found.accounts))
      .catch(() => live && setRows("failed"));
    return () => {
      live = false;
    };
  }, [user, attempt]);
  const restore = async (account: api.Account) => {
    setStatus(null);
    try {
      await api.restoreAccount(await user.getIdToken(), account.id);
      setStatus({ ok: true, text: `${account.number} ${account.name} is back in the lists.` });
      setAttempt((n) => n + 1);
      onRestored();
    } catch (e) {
      setStatus(saveFailure(e, "The restore"));
    }
  };
  return (
    <section aria-labelledby="accounts-deleted" data-testid="accounts-deleted">
      <h2 id="accounts-deleted">Deleted accounts</h2>
      {status && (
        <p role="status" data-testid="accounts-restore-status" className={status.ok ? undefined : "error"}>
          {status.text}
        </p>
      )}
      {rows === undefined ? (
        <Skeleton shape="table" columns={5} rows={3} />
      ) : rows === "failed" ? (
        <p role="alert" className="error">
          Cannot load the deleted accounts. <button onClick={() => setAttempt((n) => n + 1)}>Try again</button>
        </p>
      ) : rows.length === 0 ? (
        <p data-testid="accounts-deleted-none">No deleted accounts.</p>
      ) : (
        <div className="table-scroll" tabIndex={0} role="region" aria-label="Deleted accounts">
          <table className="list deleted-list">
            <thead>
              <tr>
                <th scope="col">Account</th>
                <th scope="col">Name</th>
                <th scope="col">Deleted</th>
                <th scope="col">Reason</th>
                <th scope="col">
                  <span className="visually-hidden">Restore</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((account) => (
                <tr key={account.id} className="deleted" data-testid="deleted-account-row">
                  <th scope="row">
                    <a href={`${base}/accounts/${account.id}`} onClick={go}>
                      {account.number}
                    </a>
                  </th>
                  <td data-label="Name">
                    <span className="clip name" title={account.name}>
                      {account.name}
                    </span>
                  </td>
                  <td data-label="Deleted" title={account.deleted ? deletedTitle(account.deleted, org.time_zone) : undefined}>
                    {account.deleted ? `${callTime(account.deleted.at, org.time_zone)} · ${account.deleted.by ? memberName(account.deleted.by) : "a removed member"}` : "–"}
                  </td>
                  <td data-label="Reason">
                    <span className="clip">{account.deleted?.reason ?? "–"}</span>
                  </td>
                  <td>
                    <button type="button" data-testid="deleted-account-restore" onClick={() => void restore(account)}>
                      Restore
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function BulkFields({ form, onChange }: { form: BulkForm; onChange: (next: BulkForm) => void }) {
  const pick = (field: api.BulkField, on: boolean) => onChange({ ...form, pick: { ...form.pick, [field]: on } });
  return (
    <>
      {BULK_FIELDS.map((field) => (
        <div key={field} className="bulk-field" data-testid={`bulk-field-${field}`}>
          <label className="check">
            <input type="checkbox" data-testid={`bulk-pick-${field}`} checked={form.pick[field]} onChange={(e) => pick(field, e.target.checked)} />{" "}
            Change the {BULK_FIELD_TEXT[field].toLowerCase()}
          </label>
          {form.pick[field] && field === "status" && (
            <select aria-label="New status" data-testid="bulk-status" value={form.status} onChange={(e) => onChange({ ...form, status: e.target.value as api.Account["status"] })}>
              {(Object.keys(STATUS_TEXT) as api.Account["status"][]).map((value) => (
                <option key={value} value={value}>
                  {STATUS_TEXT[value]}
                </option>
              ))}
            </select>
          )}
          {form.pick[field] && field === "week_group" && (
            <select aria-label="New visit weeks" data-testid="bulk-week-group" value={form.weekGroup} onChange={(e) => onChange({ ...form, weekGroup: e.target.value })}>
              {weekGroupsFor(form.weekGroup).map((group) => (
                <option key={group} value={group}>
                  {weekGroupLabel(group)}
                </option>
              ))}
            </select>
          )}
          {form.pick[field] && field === "window" && (
            <span className="bulk-window">
              <label htmlFor="bulk-window-start">From</label>
              <input id="bulk-window-start" type="time" value={form.windowStart} onChange={(e) => onChange({ ...form, windowStart: e.target.value })} />
              <label htmlFor="bulk-window-end">To</label>
              <input id="bulk-window-end" type="time" value={form.windowEnd} onChange={(e) => onChange({ ...form, windowEnd: e.target.value })} />
              <span className="sub">Leave both empty for any time.</span>
            </span>
          )}
          {form.pick[field] && field === "season" && (
            <SeasonFields start={form.seasonStart} end={form.seasonEnd} onChange={(start, end) => onChange({ ...form, seasonStart: start, seasonEnd: end })} />
          )}
          {form.pick[field] && field === "blackouts" && (
            <>
              <p className="sub">The new list takes the place of the blackout windows of each account.</p>
              <BlackoutFields blackouts={form.blackouts} onChange={(next) => onChange({ ...form, blackouts: next })} />
            </>
          )}
        </div>
      ))}
    </>
  );
}

function BulkPreview({ result }: { result: api.BulkResult }) {
  const refused = result.rows.filter((row) => row.refused).length;
  const sheet = sheetText(result.sheet, result.rows.length);
  return (
    <>
      <p role="status" data-testid="bulk-preview-summary">
        {result.rows.filter((row) => row.changed).length} of {result.rows.length} change
        {refused > 0 && `. ${refused} cannot change, so Apply stays off until you remove them.`}
      </p>
      {sheet && (
        <p className="sheet-warning" data-testid="bulk-sheet-warning">
          {sheet}
        </p>
      )}
      <div className="table-scroll" tabIndex={0} role="region" aria-label="Preview of the change">
        <table className="list bulk-preview" data-testid="bulk-preview">
          <thead>
            <tr>
              <th scope="col">Account</th>
              {result.fields.map((field) => (
                <th scope="col" key={field}>
                  {BULK_FIELD_TEXT[field]}
                </th>
              ))}
              <th scope="col">Result</th>
            </tr>
          </thead>
          <tbody>
            {result.rows.map((row) => (
              <tr key={row.account_id} data-testid="bulk-preview-row" className={row.refused ? "refused" : undefined}>
                <th scope="row">{row.number ?? "–"}</th>
                {result.fields.map((field) => (
                  <td key={field} data-label={BULK_FIELD_TEXT[field]}>
                    {row.refused ? "–" : row.before[field] === undefined ? "–" : `${valueText(field, row.before[field])} → ${valueText(field, row.after[field])}`}
                  </td>
                ))}
                <td data-label="Result">{row.refused ? <strong>{row.refused}</strong> : row.changed ? "Changes" : "No change"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function BulkEdit({
  user,
  ids,
  onClose,
  onApplied,
}: {
  user: User;
  ids: readonly string[];
  onClose: () => void;
  onApplied: (result: api.BulkResult) => void;
}) {
  const [form, setForm] = useState<BulkForm>(EMPTY_BULK);
  const [preview, setPreview] = useState<api.BulkResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => heading.current?.focus(), []);
  const idsKey = ids.join(",");
  useEffect(() => setPreview(null), [idsKey]);
  const changes = bulkChanges(form);
  const edit = (next: BulkForm) => {
    setForm(next);
    setPreview(null);
    setProblem(null);
  };
  const run = async (apply: boolean) => {
    if (typeof changes === "string") {
      setProblem(changes);
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      const token = await user.getIdToken();
      if (apply) onApplied(await api.applyBulk(token, ids, changes));
      else setPreview(await api.previewBulk(token, ids, changes));
    } catch (e) {
      setProblem(e instanceof api.ApiError && e.detail ? e.detail : SERVER_PROBLEM);
    } finally {
      setBusy(false);
    }
  };
  const ready = preview !== null && preview.rows.every((row) => !row.refused) && preview.rows.some((row) => row.changed);
  return (
    <section className="bulk-edit" aria-labelledby="bulk-edit-title" data-testid="bulk-edit">
      <h2 id="bulk-edit-title" tabIndex={-1} ref={heading}>
        {editText(ids.length)}
      </h2>
      <form
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void run(false);
        }}
      >
        <BulkFields form={form} onChange={edit} />
        <p className="actions">
          <button type="submit" data-testid="bulk-preview-button" disabled={busy}>
            Show the change
          </button>
          <button type="button" className="primary" data-testid="bulk-apply" disabled={busy || !ready} onClick={() => void run(true)}>
            Apply to {ids.length === 1 ? "1 account" : `${ids.length} accounts`}
          </button>
          <button type="button" className="quiet" data-testid="bulk-cancel" onClick={onClose}>
            Cancel
          </button>
        </p>
      </form>
      {problem && (
        <p role="alert" className="error" data-testid="bulk-problem">
          {problem}
        </p>
      )}
      {preview && <BulkPreview result={preview} />}
    </section>
  );
}

interface Done {
  changeSetId?: string;
  text: string;
}

function PinConfirm({ user, ids, count, onClose, onDone }: { user: User; ids: readonly string[]; count: number; onClose: () => void; onDone: (done: Done) => void }) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const confirm = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const found = await api.confirmPins(await user.getIdToken(), ids);
      onDone({ changeSetId: found.change_set_id, text: pinsDoneText(found.confirmed, found.placed) });
    } catch (e) {
      setProblem(e instanceof api.ApiError && e.detail ? e.detail : SERVER_PROBLEM);
      setBusy(false);
    }
  };
  return (
    <div className="bulk-confirm" role="alertdialog" aria-labelledby="pins-confirm-text" data-testid="pins-confirm">
      <p id="pins-confirm-text">{confirmPinsText(count)}</p>
      <p className="actions">
        <button type="button" className="primary" data-testid="pins-confirm-yes" disabled={busy} onClick={() => void confirm()}>
          Confirm the pins
        </button>
        <button type="button" data-testid="pins-confirm-cancel" autoFocus onClick={onClose}>
          Cancel
        </button>
      </p>
      {problem && (
        <p role="alert" className="error" data-testid="pins-problem">
          {problem}
        </p>
      )}
    </div>
  );
}

function BulkDone({ user, done, onUndone, onClose }: { user: User; done: Done; onUndone: () => void; onClose: () => void }) {
  const [state, setState] = useState<"idle" | "busy" | "undone">("idle");
  const [problem, setProblem] = useState<string | null>(null);
  const message = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    message.current?.scrollIntoView({ block: "nearest" });
    message.current?.focus();
  }, []);
  const undo = async () => {
    if (!done.changeSetId) return;
    setState("busy");
    setProblem(null);
    try {
      await api.undoBulk(await user.getIdToken(), done.changeSetId);
      setState("undone");
      onUndone();
    } catch (e) {
      setState("idle");
      setProblem(e instanceof api.ApiError && e.detail ? e.detail : SERVER_PROBLEM);
    }
  };
  return (
    <div className="bulk-done" data-testid="bulk-done" data-change-set={done.changeSetId}>
      <p role="status" tabIndex={-1} ref={message}>
        {state === "undone" ? "The change is undone." : done.text}{" "}
        {state !== "undone" && (
          <button type="button" data-testid="bulk-undo" disabled={state === "busy"} onClick={() => void undo()}>
            Undo
          </button>
        )}{" "}
        <button type="button" className="quiet" data-testid="bulk-done-close" onClick={onClose}>
          Close
        </button>
      </p>
      {problem && (
        <p role="alert" className="error" data-testid="bulk-undo-problem">
          {problem}
        </p>
      )}
    </div>
  );
}

function AccountHead({ sort, onSort }: { sort: Sort | null; onSort: (next: Sort | null) => void }) {
  return (
    <thead>
      <tr>
        {ACCOUNT_COLUMNS.map((column) => (
          <SortTh key={column.key} column={column} sort={sort} onSort={onSort} className={WRAP_HEADS.includes(column.key) ? `head-${column.key} wrap-head` : `head-${column.key}`} />
        ))}
      </tr>
    </thead>
  );
}

export function AccountList({
  base,
  slug,
  org,
  auth,
  user,
  canView,
}: {
  base: string;
  slug: string;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  canView: boolean | undefined;
}) {
  const kept = useAccounts();
  const accounts = kept.accounts;
  const canDelete = kept.extra.can_delete;
  const canBulk = kept.extra.can_bulk_edit;
  const rotation = kept.extra.rotation;
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [editing, setEditing] = useState(false);
  const [done, setDone] = useState<Done | null>(null);
  const [asking, setAsking] = useState(false);
  const [mapLoad, setMapLoad] = useState(0);
  const [showDeleted, setShowDeleted] = useState(false);
  const [checked, setChecked] = useState(false);
  const [failed, setFailed] = useState<RefreshFailure | null>(null);
  const [filter, setFilter] = useSearchState<AccountFilter>(readFilter);
  const [sort, setSort] = useSort("sort", ACCOUNT_COLUMNS);
  const [mapMode, pickMode] = useMapMode();
  const search = useRef<HTMLInputElement>(null);
  useTitle(`Accounts · ${org.name}`);
  const change = (next: Partial<AccountFilter>) => {
    const value = { ...filter, ...next };
    setFilter(value);
    const params = new URLSearchParams(writeFilter(value));
    for (const key of ["sort", MAP_PARAM, LAYER_PARAM]) {
      const kept = new URLSearchParams(location.search).get(key);
      if (kept) params.set(key, kept);
    }
    const text = params.toString();
    history.replaceState(null, "", `${location.pathname}${text ? `?${text}` : ""}`);
  };

  const checks = useRef(0);
  useEffect(() => void logAccountsLoad(slug, user.uid), [slug, user.uid]);
  const fetchAccounts = useCallback(
    async (signal: AbortSignal) => {
      const asOf = Date.now();
      const started = performance.now();
      try {
        const found = await api.accountList(await user.getIdToken(), false, signal);
        if (signal.aborted) return;
        let changed = 0;
        flushSync(() => {
          changed = keepAccounts(found, asOf);
          setChecked(true);
          setFailed(null);
        });
        logRevalidate(found.accounts.length, changed, Math.round(performance.now() - started), checks.current++ === 0);
      } catch (e) {
        if (signal.aborted) return;
        setFailed(refreshFailure(e));
      }
    },
    [user],
  );
  const sync = useSyncStatus();
  const feed = sync.mode !== "fallback";
  useEffect(() => {
    if (canView === true) wantAccounts();
  }, [canView]);
  const poll = usePoll(fetchAccounts, canView === true && !feed);
  const load = useCallback(() => (feed ? void syncNow("reload") : poll()), [feed, poll]);
  const shownFailed = feed ? sync.failed : failed;
  const firstLoad = accounts === undefined && !shownFailed;
  const shownChecked = feed ? sync.syncedAt !== null : checked;
  const shownAt = feed && sync.syncedAt !== null && kept.at !== null ? sync.syncedAt : kept.at;

  const shown = accounts ? filterAccounts(accounts, filter) : [];
  const shownIds = shown.map((account) => account.id);
  const live = new Set(accounts?.map((account) => account.id) ?? []);
  const pickedIds = [...picked].filter((id) => live.has(id));
  const onPick = canBulk ? (id: string) => setPicked((before) => toggled(before, id)) : undefined;
  const pins = pinView(filter) ? pinIds(shown, pickedIds) : [];
  const reload = () => {
    load();
    setMapLoad((count) => count + 1);
  };
  if (canView === false) {
    return (
      <main>
        <h1 tabIndex={-1}>Accounts</h1>
        <p data-testid="accounts-no-role">
          Your role does not show accounts. Ask an administrator of {org.name}.{" "}
          <a href={`${base}/tanks`} onClick={go}>
            Go to the containers
          </a>
        </p>
      </main>
    );
  }
  return (
    <main className="wide">
      <h1 tabIndex={-1}>Accounts</h1>
      <ViewSwitch map={mapMode} onPick={pickMode} />
      {(canDelete || firstLoad) && (
        <p>
          <label className="check">
            <input type="checkbox" data-testid="accounts-show-deleted" disabled={!canDelete} checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />
            Show deleted
          </label>
        </p>
      )}
      {showDeleted && <DeletedAccounts base={base} org={org} user={user} onRestored={load} />}
      {(firstLoad || (accounts && accounts.length > 0)) && (
        <form className="filters" role="search" aria-label="Filter the accounts" onSubmit={(e) => e.preventDefault()}>
          <div>
            <label htmlFor="account-search">Search</label>
            <input
              id="account-search"
              ref={search}
              type="search"
              data-testid="account-search"
              placeholder="Number, name or address"
              value={filter.q}
              onChange={(e) => change({ q: e.target.value })}
            />
          </div>
          <div>
            <label htmlFor="account-status-filter">Status</label>
            <select
              id="account-status-filter"
              data-testid="account-status-filter"
              value={filter.status}
              onChange={(e) => change({ status: e.target.value as AccountFilter["status"] })}
            >
              <option value="">All</option>
              {(Object.keys(STATUS_TEXT) as api.Account["status"][]).map((value) => (
                <option key={value} value={value}>
                  {STATUS_TEXT[value]}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="account-week-filter">Rotation week</label>
            <select
              id="account-week-filter"
              data-testid="account-week-filter"
              value={filter.week}
              onChange={(e) => change({ week: Number(e.target.value) })}
            >
              <option value={0}>All</option>
              {weekOptions(rotation).map((option) => (
                <option key={option.week} value={option.week}>
                  {option.text}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="account-source-filter">Source</label>
            <select
              id="account-source-filter"
              data-testid="account-source-filter"
              value={filter.source ?? ""}
              onChange={(e) => change({ source: e.target.value as SourceFilter })}
            >
              {SOURCE_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <label className="check">
            <input
              type="checkbox"
              data-testid="account-flagged-filter"
              checked={filter.flagged}
              onChange={(e) => change({ flagged: e.target.checked })}
            />
            Only with a flag
          </label>
          <label className="check">
            <input
              type="checkbox"
              data-testid="account-early-filter"
              checked={Boolean(filter.early)}
              onChange={(e) => change({ early: e.target.checked })}
            />
            Early pulls
          </label>
          <label className="check">
            <input
              type="checkbox"
              data-testid="account-mismatch-filter"
              checked={Boolean(filter.mismatch)}
              onChange={(e) => change({ mismatch: e.target.checked })}
            />
            Status mismatch
          </label>
          <label className="check">
            <input
              type="checkbox"
              data-testid="account-pins-filter"
              checked={Boolean(filter.pins)}
              onChange={(e) => change({ pins: e.target.checked })}
            />
            <HelpText text="Geocoded front doors that nobody has checked. A rough geocode shows first on the map.">Pins to check</HelpText>
          </label>
          <label className="check">
            <input
              type="checkbox"
              data-testid="account-rough-filter"
              checked={Boolean(filter.rough)}
              onChange={(e) => change({ rough: e.target.checked })}
            />
            <HelpText text="Rough geocodes of the front door. A person must check each pin.">Rough pins</HelpText>
          </label>
          <label className="check">
            <input
              type="checkbox"
              data-testid="account-noplan-filter"
              checked={Boolean(filter.noPlan)}
              onChange={(e) => change({ noPlan: e.target.checked })}
            />
            <HelpText text="Accounts with no service plan, thus no area. Give each one a plan, or move its pin into an area shape.">No service plan</HelpText>
          </label>
          {isFiltered(filter) && (
            <button
              type="button"
              className="quiet"
              data-testid="clear-filters"
              onClick={() => {
                change(NO_FILTER);
                search.current?.focus();
              }}
            >
              Clear the filters
            </button>
          )}
        </form>
      )}
      {accounts && accounts.length > 0 && rotation && (
        <WeekTabs
          tabs={weekTabs({ week: rotation.this_week, week_start: rotation.week_start, this_week: rotation.this_week }, rotation.holidays ?? [])}
          week={filter.week || undefined}
          onPick={(week) => change({ week: filter.week === week ? 0 : week })}
          testId="account-week-tab"
          label="Rotation weeks: a click sets the Rotation week filter"
          small
        />
      )}
      {firstLoad && !mapMode && <div className="skeleton-week-tabs" />}
      <p className="freshness" data-testid="freshness">
        {accounts && (
          <strong role="status" data-testid="account-summary">
            {isFiltered(filter) ? `${shown.length} of ${accountCount(accounts.length)}` : accountCount(accounts.length)} ·{" "}
            {nearFullCount(shown)} near full in the rotation
            {accounts.length > 0 && shown.length === 0 && (
              <span className="visually-hidden">. No accounts match the filters.</span>
            )}
          </strong>
        )}
        {accounts && (shownFailed || shownChecked || shownAt === null) && " · "}
        {shownAt === null ? (
          shownFailed ? "" : <SkeletonBar width="9rem" />
        ) : shownFailed ? (
          <span data-testid="accounts-stale">{failedText(shownAt, org.time_zone)}</span>
        ) : shownChecked ? (
          <span data-testid="accounts-updated">Updated {clockTime(new Date(shownAt), org.time_zone)}</span>
        ) : (
          <span data-testid="accounts-kept">
            {accounts && " · "}
            {keptText(shownAt)}
          </span>
        )}
      </p>
      {accounts && accounts.length > 0 && (
        <p className="footnote">Estimated load and Full are the fill estimate of each container. Weeks left uses the pickups and the stated rate, as in the service sheet.</p>
      )}
      {shownFailed && <RefreshFailed failed={shownFailed} at={shownAt} timeZone={org.time_zone} auth={auth} retry={load} />}
      {firstLoad && !mapMode && (
        <>
          <p className="footnote">
            <SkeletonBar width="60%" />
          </p>
          <SkeletonTools />
          <Skeleton shape="table" columns={8} rows={8} />
        </>
      )}
      {accounts?.length === 0 && <p data-testid="accounts-empty">No accounts yet.</p>}
      {accounts && accounts.length > 0 && shown.length === 0 && (
        <p data-testid="accounts-no-match">No accounts match the filters.</p>
      )}
      {done && (
        <BulkDone
          key={done.changeSetId}
          user={user}
          done={done}
          onUndone={reload}
          onClose={() => setDone(null)}
        />
      )}
      {(!mapMode || pinView(filter)) && canBulk && shown.length > 0 && (
        <p className="bulk-bar" data-testid="bulk-bar">
          <label className="check">
            <input
              type="checkbox"
              data-testid="account-pick-all"
              checked={allPicked(picked, shownIds)}
              onChange={(e) => setPicked((before) => withAll(before, shownIds, e.target.checked))}
            />{" "}
            Select all shown ({shown.length})
          </label>{" "}
          <span data-testid="bulk-count">{pickedIds.length} selected</span>{" "}
          {!mapMode && (
            <button
              type="button"
              data-testid="bulk-open"
              disabled={editOff(pickedIds.length)}
              onClick={() => {
                setDone(null);
                setAsking(false);
                setEditing(true);
              }}
            >
              {editText(pickedIds.length)}
            </button>
          )}
          {pinView(filter) && (
            <>
              {" "}
              <button
                type="button"
                data-testid="bulk-confirm-pins"
                disabled={pinsOff(pins.length)}
                onClick={() => {
                  setDone(null);
                  setEditing(false);
                  setAsking(true);
                }}
              >
                Confirm the pins
              </button>
            </>
          )}
          {pickedIds.length > 0 && (
            <>
              {" "}
              <button type="button" className="quiet" data-testid="bulk-clear" onClick={() => setPicked(new Set())}>
                Clear the selection
              </button>
            </>
          )}
        </p>
      )}
      {asking && pins.length > 0 && (
        <PinConfirm
          user={user}
          ids={pins}
          count={pins.length}
          onClose={() => setAsking(false)}
          onDone={(next) => {
            setAsking(false);
            setDone(next);
            setPicked(new Set());
            reload();
          }}
        />
      )}
      {!mapMode && editing && pickedIds.length > 0 && (
        <BulkEdit
          user={user}
          ids={pickedIds}
          onClose={() => setEditing(false)}
          onApplied={(result) => {
            setEditing(false);
            setDone({ changeSetId: result.change_set_id, text: changedText(result.rows.filter((row) => row.changed).length) });
            setPicked(new Set());
            load();
          }}
        />
      )}
      {mapMode && canView === true && <PinMap kind="accounts" filters={accountPinFilters(filter)} base={base} auth={auth} user={user} reload={mapLoad} timeZone={org.time_zone} />}
      {!mapMode && pinView(filter) && shown.length > 0 && <PinList accounts={shown} base={base} picked={picked} onPick={onPick} />}
      {!mapMode && !pinView(filter) && shown.length > 0 && (
        <SortSelect id="account-sort" columns={ACCOUNT_COLUMNS} sort={sort} onSort={setSort} fallback="By rotation week and area" />
      )}
      {!mapMode && !pinView(filter) && shown.length > 0 && sort && (
        <div data-testid="account-list">
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Accounts">
            <table className="accounts list" data-cols="account-sort">
              <AccountHead sort={sort} onSort={setSort} />
              <tbody>
                {sortRows(shown, ACCOUNT_COLUMNS, sort).map((account) => (
                  <AccountRow key={account.id} account={account} base={base} picked={picked.has(account.id)} onPick={onPick} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {!mapMode && !pinView(filter) && shown.length > 0 && !sort && (
        <div data-testid="account-list">
          {sectionsFor(groupAccounts(shown), filter).map((section) => (
            <section key={section.key} aria-labelledby={`accounts-${section.key}`}>
              <h2 id={`accounts-${section.key}`}>{section.title}</h2>
              <div className="table-scroll" tabIndex={0} role="region" aria-label={section.label}>
                <table className="accounts list" data-cols="account-sort">
                  <AccountHead sort={sort} onSort={setSort} />
                  {section.groups.map((group) => (
                    <tbody key={group.title}>
                      {group.title && (
                        <tr className="area-row">
                          <th scope="colgroup" colSpan={ACCOUNT_SPAN}>
                            {group.title}
                          </th>
                        </tr>
                      )}
                      {group.accounts.map((account) => (
                        <AccountRow key={account.id} account={account} base={base} picked={picked.has(account.id)} onPick={onPick} />
                      ))}
                    </tbody>
                  ))}
                </table>
              </div>
            </section>
          ))}
        </div>
      )}
    </main>
  );
}

const NEW_LOAD = "new";
/** The 409 text of Record a pickup: the reader is the office, so it gives the next step (SO-1013). */
const OFFICE_CONFLICT = "The server has this pickup already. Reload the page to see it in the pickup list.";
const OFFICE_CORRECTED = "The server has a correction of this pickup already. Reload the page to see it in the pickup list.";

function PickupForm({
  account,
  org,
  auth,
  user,
  correcting,
  quiet,
  onCancel,
  onSaved,
}: {
  account: api.AccountDetail;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  correcting: api.Pickup | null;
  quiet: boolean;
  onCancel: () => void;
  onSaved: (next: api.AccountDetail) => void;
}) {
  const today = todayIn(org.time_zone);
  const [day, setDay] = useState(correcting?.at ?? today);
  const [gallons, setGallons] = useState(correcting ? String(correcting.raw_gal) : "");
  const [scrap, setScrap] = useState(correcting ? String(correcting.scrap_gal) : "");
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const [problems, setProblems] = useState<{ date?: string; gallons?: string; scrap?: string; warning?: string; truck?: string }>({});
  const [load, setLoad] = useState("");
  const [truck, setTruck] = useState("");
  const loadId = useRef<string | null>(null);
  const entryId = useRef<string | null>(null);
  const warned = useRef<string | null>(null);
  const field = useRef<HTMLInputElement>(null);
  const scrapOk = useRef(false);
  const [askNoScrap, setAskNoScrap] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [reloading, setReloading] = useState(false);
  useEffect(() => {
    setConflict(false);
    setDay(correcting?.at ?? todayIn(org.time_zone));
    setGallons(correcting ? String(correcting.raw_gal) : "");
    setScrap(correcting ? String(correcting.scrap_gal) : "");
    entryId.current = null;
    warned.current = null;
    setProblems({});
    if (!correcting) return;
    setSaved(null);
    field.current?.focus();
  }, [correcting, org.time_zone]);

  const parsed = parseGallons(gallons, scrap);
  const edit = (next: () => void) => {
    next();
    entryId.current = null;
    warned.current = null;
    scrapOk.current = false;
    setAskNoScrap(false);
    if (!conflict) setSaved(null);
    setProblems({});
  };
  const reload = async () => {
    setReloading(true);
    try {
      const next = await api.account(await user.getIdToken(), account.id);
      console.info(`pickup form: account ${account.id} is reloaded after a 409, Save is on again`);
      setConflict(false);
      entryId.current = null;
      setGallons("");
      setScrap("");
      setSaved(null);
      onSaved(next);
    } catch (e) {
      setSaved(saveFailure(e, "The reload"));
    } finally {
      setReloading(false);
    }
  };
  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (conflict) {
      console.warn(`pickup form: pickup ${entryId.current} is not sent again, the server answered 409; reload first`);
      return;
    }
    const dateProblem = dateError(day, today);
    const newLoad = !correcting && load === NEW_LOAD;
    const truckProblem = newLoad ? truckError(truck) : "";
    if (dateProblem || "error" in parsed || truckProblem) {
      setSaved(null);
      setProblems({
        date: dateProblem || undefined,
        gallons: "error" in parsed && parsed.field === "total" ? parsed.error : undefined,
        scrap: "error" in parsed && parsed.field === "scrap" ? parsed.error : undefined,
        truck: truckProblem || undefined,
      });
      return;
    }
    if (asksNoScrap(parsed.raw, parsed.scrap) && !scrapOk.current) {
      setSaved(null);
      setAskNoScrap(true);
      requestAnimationFrame(() => document.getElementById("pickup-no-scrap-yes")?.focus());
      return;
    }
    setAskNoScrap(false);
    const warning = capacityWarning(parsed, account.capacity_gal);
    if (warning && warned.current !== `${day} ${gallons} ${scrap}`) {
      warned.current = `${day} ${gallons} ${scrap}`;
      setSaved(null);
      setProblems({ warning });
      return;
    }
    setProblems({});
    entryId.current ??= crypto.randomUUID();
    setSaving(true);
    setSaved(null);
    try {
      const entry: api.PickupEntry = { id: entryId.current, at: day, raw_gal: parsed.raw, scrap_gal: parsed.scrap };
      if (correcting) entry.corrects_id = correcting.id;
      const token = await user.getIdToken();
      if (newLoad) {
        loadId.current ??= crypto.randomUUID();
        await api.makeLoad(token, { id: loadId.current, day: today, truck: cleanTruck(truck) });
        entry.load_id = loadId.current;
      } else if (load && !correcting) {
        entry.load_id = load;
      }
      const next = await api.recordPickup(token, account.id, entry);
      entryId.current = null;
      scrapOk.current = false;
      if (entry.load_id) setLoad(entry.load_id);
      loadId.current = null;
      setTruck("");
      setGallons("");
      setScrap("");
      setDay(today);
      onSaved(next);
      const net = galText(Math.max(0, parsed.raw - parsed.scrap));
      const onLoad = next.pickups.find((row) => row.id === entry.id)?.load?.code;
      setSaved({
        ok: true,
        text: `${correcting ? "Corrected" : "Saved"}: ${galText(parsed.raw)} raw, ${galText(parsed.scrap)} scrap, ${net} net on ${dayText(day)}${onLoad ? `, on load ${onLoad}` : ""}.`,
      });
    } catch (e) {
      const office = e instanceof api.ApiError && e.status === 409;
      const failure = plainFailure(e);
      setSaved(office && !failure.ok ? { ...failure, text: correcting ? OFFICE_CORRECTED : OFFICE_CONFLICT, at: null } : failure);
      if (office) {
        setConflict(true);
        requestAnimationFrame(() => document.querySelector<HTMLButtonElement>('[data-testid="pickup-reload"]')?.focus());
      }
      const at = !failure.ok && !office ? pickupField(failure.at) : null;
      if (e instanceof api.ApiError)
        console.warn(`pickup form: pickup ${entryId.current} is not saved, status ${e.status}, field ${at ?? "none"}: ${e.server ?? "no server text"}`);
      if (at) requestAnimationFrame(() => document.getElementById(`pickup-${at}`)?.focus());
    } finally {
      setSaving(false);
    }
  };

  const refusal = saved && !saved.ok && saved.server !== undefined ? saved : null;
  const refusedAt = pickupField(refusal?.at);
  const saveLabel = correcting ? "Save the correction" : "Save the pickup";
  const refusedText = refusal?.at && refusedAt ? fieldFixText(refusal.at, "gallons", { button: saveLabel, time: "date" }) : null;
  const fieldError = saved && !saved.ok && saved.field ? saved.text : null;
  const dateNote = problems.date ?? (refusedAt === "date" ? refusedText : null);
  const scrapError = problems.scrap ?? (refusedAt === "scrap" ? refusedText : null);
  const gallonsError = problems.gallons ?? fieldError ?? (refusedAt === "gallons" ? refusedText : null);
  const gallonsNote = gallonsError ?? problems.warning;
  const hint = "error" in parsed ? "Net gallons: total less scrap." : `Net: ${galText(parsed.raw - parsed.scrap)}`;
  return (
    <section aria-labelledby="pickup-form-title">
      <h2 id="pickup-form-title">{correcting ? `Correct the pickup of ${dayText(correcting.at)}` : "Record a pickup"}</h2>
      <form onSubmit={submit} noValidate data-testid="pickup-form">
        <label htmlFor="pickup-date">Date</label>
        <input
          id="pickup-date"
          type="date"
          data-testid="pickup-date"
          value={day}
          max={today}
          onChange={(e) => edit(() => setDay(e.target.value))}
          aria-describedby={dateNote ? "pickup-date-error" : undefined}
          aria-invalid={dateNote ? true : undefined}
        />
        {dateNote && (
          <p id="pickup-date-error" role="alert" className="error" data-testid="pickup-date-error">
            {dateNote}
          </p>
        )}
        <GallonsFields
          id="pickup"
          testId="pickup"
          word="gallons"
          total={gallons}
          scrap={scrap}
          onTotal={(text) => edit(() => setGallons(text))}
          onScrap={(text) => edit(() => setScrap(text))}
          totalRef={field}
          totalAria={{ "aria-describedby": gallonsNote ? "pickup-error pickup-hint" : "pickup-hint", "aria-invalid": gallonsError ? true : undefined }}
          scrapAria={scrapError ? { "aria-describedby": "pickup-error pickup-hint", "aria-invalid": true } : { "aria-describedby": "pickup-hint" }}
        >
          {(gallonsNote ?? scrapError) && (
            <p id="pickup-error" role="alert" className="error" data-testid="pickup-error">
              {gallonsNote ?? scrapError}
            </p>
          )}
        </GallonsFields>
        {askNoScrap && (
          <NoScrapConfirm
            id="pickup"
            testId="pickup"
            disabled={saving}
            onYes={() => {
              scrapOk.current = true;
              void submit();
            }}
            onChange={() => {
              setAskNoScrap(false);
              requestAnimationFrame(() => document.getElementById("pickup-scrap")?.focus());
            }}
          />
        )}
        <p id="pickup-hint" className="hint" aria-live="polite">
          {hint}
        </p>
        {!correcting && account.loads !== null && (account.loads.length > 0 || account.can_make_load) && (
          <>
            <label htmlFor="pickup-load">Load (optional)</label>
            <select id="pickup-load" data-testid="pickup-load" value={load} onChange={(e) => edit(() => setLoad(e.target.value))}>
              <option value="">No load</option>
              {account.loads.map((choice) => (
                <option key={choice.id} value={choice.id}>
                  Load {choice.code}
                </option>
              ))}
              {account.can_make_load && <option value={NEW_LOAD}>A new load for today</option>}
            </select>
            {load === NEW_LOAD && (
              <>
                <label htmlFor="pickup-truck">Truck</label>
                <input
                  id="pickup-truck"
                  data-testid="pickup-truck"
                  autoComplete="off"
                  placeholder="T7"
                  value={truck}
                  onChange={(e) => edit(() => setTruck(e.target.value))}
                  aria-describedby={problems.truck ? "pickup-truck-error" : undefined}
                  aria-invalid={problems.truck ? true : undefined}
                />
                {problems.truck && (
                  <p id="pickup-truck-error" role="alert" className="error" data-testid="pickup-truck-error">
                    {problems.truck}
                  </p>
                )}
              </>
            )}
          </>
        )}
        <div className="actions">
          <button className={quiet ? undefined : "primary"} data-testid="save-pickup" disabled={saving || conflict}>
            {saving ? "Saving…" : saveLabel}
          </button>
          {correcting && (
            <button type="button" data-testid="cancel-correction" disabled={saving} onClick={onCancel}>
              Cancel
            </button>
          )}
        </div>
      </form>
      <p role="status" data-testid="pickup-status" className={saved && !saved.ok && !saved.field ? "error" : undefined}>
        {saved && !fieldError && !refusedText && (refusal ? `Not saved. ${saved.text}` : saved.text)}
        {conflict && (
          <>
            {" "}
            <button type="button" data-testid="pickup-reload" disabled={reloading} onClick={() => void reload()}>
              {reloading ? "Reloading…" : "Reload"}
            </button>
          </>
        )}
        {saved && !saved.ok && saved.session && (
          <>
            {" "}
            <button onClick={() => signOut(auth)}>Sign in again</button>
          </>
        )}
      </p>
      {refusal?.server && (
        <details className="sub" data-testid="pickup-server">
          <summary>Server text, for the office</summary>
          {refusal.server}
        </details>
      )}
    </section>
  );
}

function CallForm({
  account,
  auth,
  user,
  quiet,
  onSaved,
  onOpenIssue,
  leadBase,
}: {
  account: api.AccountDetail;
  auth: Auth;
  user: User;
  quiet: boolean;
  onSaved: (next: api.AccountDetail) => void;
  onOpenIssue?: (callText: string) => void;
  leadBase?: string;
}) {
  const [reason, setReason] = useState<Reason | "">("");
  const lastCall = useRef("");
  const [caller, setCaller] = useState("");
  const [result, setResult] = useState("");
  const [atFault, setAtFault] = useState(false);
  const [makeRequest, setMakeRequest] = useState(true);
  const [saved, setSaved] = useState<Saved | null>(null);
  const [badField, setBadField] = useState<"reason" | "caller" | "result">("caller");
  const current = openRequest(account.pickup_requests);
  const asks = reason === "pickup_request" && Boolean(account.can_request_pickup) && current === null;
  const [saving, setSaving] = useState(false);
  const entryId = useRef<string | null>(null);
  const reasonRef = useRef<HTMLSelectElement>(null);
  const edit = (next: () => void) => {
    next();
    entryId.current = null;
    setSaved(null);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!reason) {
      setBadField("reason");
      setSaved({ ok: false, field: true, text: "Choose the reason of the call." });
      reasonRef.current?.focus();
      return;
    }
    const problem = callProblem(caller, result);
    if (problem) {
      setBadField(problem.field);
      setSaved({ ok: false, field: true, text: problem.text });
      return;
    }
    entryId.current ??= crypto.randomUUID();
    setSaving(true);
    setSaved(null);
    try {
      const entry: api.CallEntry = { id: entryId.current, caller: caller.trim(), reason, at_fault: atFault, result: result.trim(), make_request: asks && makeRequest };
      const next = await api.logCall(await user.getIdToken(), account.id, entry);
      entryId.current = null;
      setCaller("");
      setResult("");
      setAtFault(false);
      setReason("");
      setMakeRequest(true);
      onSaved(next);
      lastCall.current = `${REASON_TEXT[entry.reason]} call from ${entry.caller}${entry.result ? `: ${entry.result}` : "."}`;
      const made = next.pickup_requests?.find((request) => request.call_id === entry.id);
      const request = made ? ` Pickup request by ${shortDay(made.need_by)}.` : "";
      setSaved({ ok: true, text: `Saved: ${REASON_TEXT[entry.reason]} call from ${entry.caller}${entry.at_fault ? ", at fault" : ""}.${request}` });
      requestAnimationFrame(() => reasonRef.current?.focus());
    } catch (e) {
      const failure = saveFailure(e, "The call");
      // Only a 422 is about the fields; a 403 or a 409 goes in the status line.
      const status = e instanceof api.ApiError ? e.status : 0;
      setBadField("caller");
      setSaved(!failure.ok && status !== 422 ? { ...failure, field: false } : failure);
    } finally {
      setSaving(false);
    }
  };
  const fieldError = saved && !saved.ok && saved.field ? saved.text : null;
  const reasonError = badField === "reason" ? fieldError : null;
  const callerError = badField === "caller" ? fieldError : null;
  const resultError = badField === "result" ? fieldError : null;
  return (
    <form onSubmit={submit} noValidate data-testid="call-form" aria-labelledby="call-form-title">
      <h3 id="call-form-title">Log a call</h3>
      <label htmlFor="call-reason">Reason</label>
      <select
        ref={reasonRef}
        id="call-reason"
        data-testid="call-reason"
        value={reason}
        onChange={(e) =>
          edit(() => {
            setReason(e.target.value as Reason | "");
            setMakeRequest(true);
          })
        }
        aria-describedby={reasonError ? "call-reason-error" : undefined}
        aria-invalid={reasonError ? true : undefined}
      >
        <option value="" disabled>
          Choose a reason
        </option>
        {REASONS.map((value) => (
          <option key={value} value={value}>
            {REASON_TEXT[value]}
          </option>
        ))}
      </select>
      {reasonError && (
        <p id="call-reason-error" role="alert" className="error" data-testid="call-error">
          {reasonError}
        </p>
      )}
      <label htmlFor="call-caller">Caller</label>
      <input
        id="call-caller"
        data-testid="call-caller"
        autoComplete="off"
        value={caller}
        onChange={(e) => edit(() => setCaller(e.target.value))}
        aria-describedby={callerError ? "call-error" : undefined}
        aria-invalid={callerError ? true : undefined}
      />
      {callerError && (
        <p id="call-error" role="alert" className="error" data-testid="call-error">
          {callerError}
        </p>
      )}
      <label htmlFor="call-result">Result</label>
      <textarea
        id="call-result"
        data-testid="call-result"
        rows={2}
        value={result}
        onChange={(e) => edit(() => setResult(e.target.value))}
        aria-describedby={resultError ? "call-result-error" : undefined}
        aria-invalid={resultError ? true : undefined}
      />
      {resultError && (
        <p id="call-result-error" role="alert" className="error" data-testid="call-error">
          {resultError}
        </p>
      )}
      <label className="check">
        <input type="checkbox" data-testid="call-at-fault" checked={atFault} onChange={(e) => edit(() => setAtFault(e.target.checked))} />
        At fault: we caused the problem, for example a missed or late stop
      </label>
      {asks && (
        <label className="check">
          <input type="checkbox" data-testid="call-make-request" checked={makeRequest} onChange={(e) => edit(() => setMakeRequest(e.target.checked))} />
          Make a request: the planner puts the account on a route within {NEED_DAYS} days
        </label>
      )}
      {reason === "pickup_request" && current && (
        <p className="sub" data-testid="call-open-request">
          The account has an open request, need by {shortDay(current.need_by)}. The call adds no second request.
        </p>
      )}
      {reason === "pickup_request" && !current && !account.can_request_pickup && (
        <p className="sub" data-testid="call-no-request">
          This call makes no pickup request. Ask the office to make one.
        </p>
      )}
      <div className="actions">
        <button className={quiet ? undefined : "primary"} data-testid="save-call" disabled={saving}>
          {saving ? "Saving…" : "Save the call"}
        </button>
      </div>
      <p role="status" data-testid="call-status" className={saved && !saved.ok && !saved.field ? "error" : undefined}>
        {saved && !fieldError && saved.text}
        {saved?.ok && onOpenIssue && (
          <>
            {" "}
            <button type="button" className="link" data-testid="call-open-issue" onClick={() => onOpenIssue(lastCall.current)}>
              Open an issue
            </button>
          </>
        )}
        {saved?.ok && leadBase && (
          <>
            {" "}
            <a
              href={callLeadPath(leadBase)}
              onClick={(event) => {
                keepCallNote(`${account.number} ${account.name}: ${lastCall.current}`);
                go(event);
              }}
              data-testid="call-make-lead"
            >
              Make a lead
            </a>
          </>
        )}
        {saved && !saved.ok && saved.session && (
          <>
            {" "}
            <button type="button" onClick={() => signOut(auth)}>
              Sign in again
            </button>
          </>
        )}
      </p>
    </form>
  );
}

const STATUSES: api.Account["status"][] = ["NEW", "REG", "CALL", "HOLD", "OFF"];
/** Two holds sit side by side in the form, so each option says what it stops. */
const SERVICE_OPTION_TEXT: Record<api.Account["status"], string> = { ...STATUS_TEXT, HOLD: "On hold (no pickups)" };
const COMMERCIAL_OPTION_TEXT: Record<api.Commercial, string> = { ...COMMERCIAL_TEXT, hold: "Commercial hold (pickups continue)" };

const DEFAULT_SEASON: [string, string] = ["04-01", "10-31"];

function MonthDayPicker({ id, label, value, onChange }: { id: string; label: string; value: string; onChange: (next: string) => void }) {
  const [month, day] = value.split("-").map(Number);
  const pick = (nextMonth: number, nextDay: number) => onChange(monthDay(nextMonth, keptDay(nextMonth, nextDay)) ?? value);
  return (
    <span className="month-day">
      <label htmlFor={`${id}-month`}>{label}</label>
      <select id={`${id}-month`} data-testid={`${id}-month`} value={month} onChange={(e) => pick(Number(e.target.value), day)}>
        {MONTHS.map((name, index) => (
          <option key={name} value={index + 1}>
            {name}
          </option>
        ))}
      </select>
      <select id={`${id}-day`} data-testid={`${id}-day`} aria-label={`${label}, day`} value={day} onChange={(e) => pick(month, Number(e.target.value))}>
        {Array.from({ length: 31 }, (_, index) => index + 1).map((number) => (
          <option key={number} value={number} disabled={monthDay(month, number) === null}>
            {number}
          </option>
        ))}
      </select>
    </span>
  );
}

function SeasonFields({ start, end, onChange }: { start: string; end: string; onChange: (start: string, end: string) => void }) {
  const allYear = !start || !end;
  return (
    <fieldset className="season" data-testid="service-season">
      <legend>Season</legend>
      <label className="check">
        <input
          type="checkbox"
          data-testid="season-all-year"
          checked={allYear}
          onChange={(e) => (e.target.checked ? onChange("", "") : onChange(...DEFAULT_SEASON))}
        />{" "}
        All year
      </label>
      {!allYear && (
        <>
          <MonthDayPicker id="season-start" label="First day" value={start} onChange={(next) => onChange(next, end)} />
          <MonthDayPicker id="season-end" label="Last day" value={end} onChange={(next) => onChange(start, next)} />
          <p className="sub">Out of season, the board does not choose the account and the forecast stops.</p>
        </>
      )}
    </fieldset>
  );
}

function BlackoutFields({ blackouts, onChange }: { blackouts: api.Blackout[]; onChange: (next: api.Blackout[]) => void }) {
  const change = (index: number, item: Partial<api.Blackout>) => onChange(blackouts.map((found, at) => (at === index ? { ...found, ...item } : found)));
  return (
    <fieldset className="blackouts" data-testid="service-blackouts">
      <legend>Blackout windows (the driver must not arrive)</legend>
      {blackouts.length === 0 && <p className="sub">None</p>}
      {blackouts.map((item, index) => (
        <div className="blackout-row" key={index} data-testid="blackout-row">
          <label htmlFor={`blackout-day-${index}`}>Day</label>
          <select id={`blackout-day-${index}`} value={item.day} onChange={(e) => change(index, { day: Number(e.target.value) })}>
            {DAY_NAMES.map((name, day) => (
              <option key={name} value={day}>
                {name}
              </option>
            ))}
          </select>
          <label htmlFor={`blackout-start-${index}`}>From</label>
          <input
            id={`blackout-start-${index}`}
            inputMode="numeric"
            placeholder="HH:MM"
            autoComplete="off"
            value={item.start}
            onChange={(e) => change(index, { start: e.target.value })}
            onBlur={(e) => change(index, { start: clockText(e.target.value) })}
          />
          <label htmlFor={`blackout-end-${index}`}>To</label>
          <input
            id={`blackout-end-${index}`}
            inputMode="numeric"
            placeholder="HH:MM"
            autoComplete="off"
            value={item.end}
            onChange={(e) => change(index, { end: e.target.value })}
            onBlur={(e) => change(index, { end: clockText(e.target.value) })}
          />
          <button type="button" data-testid="blackout-remove" onClick={() => onChange(blackouts.filter((_, at) => at !== index))}>
            Remove
          </button>
        </div>
      ))}
      {blackouts.length < MAX_BLACKOUTS && (
        <button type="button" data-testid="blackout-add" onClick={() => onChange([...blackouts, { day: 0, start: "11:00", end: "13:00" }])}>
          Add a blackout window
        </button>
      )}
    </fieldset>
  );
}

/** Rule T-5: a field that the sheet holds. */
function SheetNote({ held }: { held: boolean }) {
  return held ? (
    <span className="from-sheet" data-testid="from-sheet">
      From the sheet
    </span>
  ) : null;
}

/** Rule T-5: the screen can change the field, but the next import writes the sheet value. */
function SheetWarning({ held }: { held: boolean }) {
  return held ? (
    <p className="sheet-warning" data-testid="sheet-warning">
      {SHEET_WARNING}
    </p>
  ) : null;
}

function ServiceForm({
  account,
  auth,
  user,
  onClose,
  onSaved,
}: {
  account: api.AccountDetail;
  auth: Auth;
  user: User;
  onClose: () => void;
  onSaved: (next: api.AccountDetail, undo: api.AccountChange | null) => void;
}) {
  const [status, setStatus] = useState(account.status);
  const [commercial, setCommercial] = useState(account.commercial_status);
  const held = (field: string) => sheetHeld(account.from_sheet, field);
  const [form, setForm] = useState(() => planForm(account));
  const [areas, setAreas] = useState<api.Area[] | "failed" | undefined>(undefined);
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const first = useRef<HTMLSelectElement>(null);
  useEffect(() => {
    first.current?.focus();
    user
      .getIdToken()
      .then(api.areas)
      .then(setAreas, () => setAreas("failed"));
  }, [user]);

  const set = (field: keyof typeof form) => (value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setSaved(null);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const plan = planChange(form, Array.isArray(areas) ? areas : []);
    if (typeof plan === "string") {
      setSaved({ ok: false, field: true, text: plan });
      return;
    }
    if (plan === null && account.plan) {
      setSaved({ ok: false, field: true, text: "Choose an area. An account cannot lose its service plan here." });
      return;
    }
    setSaving(true);
    setSaved(null);
    try {
      const change: api.AccountChange = { status, ...(plan ? { plan } : {}), ...(commercial ? { commercial_status: commercial } : {}) };
      const next = await api.changeAccount(await user.getIdToken(), account.id, change);
      onSaved(next, undoChange(account, next));
    } catch (e) {
      setSaved(saveFailure(e, "The change"));
    } finally {
      setSaving(false);
    }
  };
  const fieldError = saved && !saved.ok && saved.field ? saved.text : null;
  const area = Array.isArray(areas) ? areas.find((found) => found.id === form.areaId) : undefined;
  const pinArea = Array.isArray(areas) ? areaOfPoint(areas, account.tank ?? account.door ?? null) : null;
  return (
    <form onSubmit={submit} noValidate data-testid="service-form" aria-labelledby="service-form-title">
      <h3 id="service-form-title">Change the service</h3>
      {Boolean(account.from_sheet?.length) && (
        <p className="sub" data-testid="service-from-sheet">
          The fields marked &quot;From the sheet&quot; come from the sheet import. You can change them here, but the import writes the sheet
          value again when the sheet changes, and each night.
        </p>
      )}
      <label htmlFor="service-status">
        Service status <SheetNote held={held("status")} />
      </label>
      <select
        id="service-status"
        ref={first}
        data-testid="service-status"
        value={status}
        onChange={(e) => {
          setStatus(e.target.value as api.Account["status"]);
          setSaved(null);
        }}
      >
        {STATUSES.map((value) => (
          <option key={value} value={value}>
            {SERVICE_OPTION_TEXT[value]}
          </option>
        ))}
      </select>
      <SheetWarning held={held("status")} />
      {commercial && (
        <>
          <label htmlFor="service-commercial">
            Commercial status (the contract) <SheetNote held={held("commercial_status")} />
          </label>
          <select
            id="service-commercial"
            data-testid="service-commercial"
            value={commercial}
            onChange={(e) => {
              setCommercial(e.target.value as api.Commercial);
              setSaved(null);
            }}
          >
            {COMMERCIALS.map((value) => (
              <option key={value} value={value}>
                {COMMERCIAL_OPTION_TEXT[value]}
              </option>
            ))}
          </select>
          <SheetWarning held={held("commercial_status")} />
        </>
      )}
      <label htmlFor="service-area">
        Area <SheetNote held={held("area_id")} />
      </label>
      <select
        id="service-area"
        data-testid="service-area"
        value={form.areaId}
        disabled={!Array.isArray(areas)}
        onChange={(e) => set("areaId")(e.target.value)}
      >
        <option value="">{areas === undefined ? "Loading…" : areas === "failed" ? "Cannot load the areas" : "No service plan"}</option>
        {Array.isArray(areas) &&
          areas.map((found) => (
            <option key={found.id} value={found.id}>
              {found.code} · rotation week {found.rotation_week}
            </option>
          ))}
      </select>
      <SheetWarning held={held("area_id")} />
      {pinArea && pinArea.id !== form.areaId && (
        <p className="sub" data-testid="service-pin-area">
          The pin is in the shape of area {pinArea.code}.{" "}
          <button type="button" className="link" data-testid="service-use-pin-area" onClick={() => set("areaId")(pinArea.id)}>
            Use area {pinArea.code}
          </button>
        </p>
      )}
      {areas === "failed" && (
        <p className="error" role="alert" data-testid="service-areas-failed">
          The areas did not load. Reload the page.
        </p>
      )}
      {form.areaId && (
        <>
          <label htmlFor="service-weeks">
            Visits <SheetNote held={held("week_group")} />
          </label>
          <select
            id="service-weeks"
            value={form.weekGroup || String(area?.rotation_week ?? "")}
            onChange={(e) => set("weekGroup")(e.target.value)}
          >
            {weekGroupsFor(account.plan?.week_group ?? "").map((group) => (
              <option key={group} value={group}>
                {weekGroupLabel(group)}
              </option>
            ))}
          </select>
          <SheetWarning held={held("week_group")} />
          <fieldset className="window">
            <legend>
              Service window <SheetNote held={held("window")} />
            </legend>
            <label htmlFor="service-start">From</label>
            <input id="service-start" type="time" value={form.start} onChange={(e) => set("start")(e.target.value)} />
            <label htmlFor="service-end">To</label>
            <input id="service-end" type="time" value={form.end} onChange={(e) => set("end")(e.target.value)} />
            <SheetWarning held={held("window")} />
          </fieldset>
          <SeasonFields
            start={form.seasonStart}
            end={form.seasonEnd}
            onChange={(start, end) => {
              setForm((current) => ({ ...current, seasonStart: start, seasonEnd: end }));
              setSaved(null);
            }}
          />
          <BlackoutFields
            blackouts={form.blackouts}
            onChange={(blackouts) => {
              setForm((current) => ({ ...current, blackouts }));
              setSaved(null);
            }}
          />
          <label htmlFor="service-minutes">
            Minutes at the stop <SheetNote held={held("service_minutes")} />
          </label>
          <input
            id="service-minutes"
            inputMode="numeric"
            value={form.minutes}
            onChange={(e) => set("minutes")(e.target.value)}
          />
          <SheetWarning held={held("service_minutes")} />
          <label htmlFor="service-rate">
            Stated rate (gallons each week) <SheetNote held={held("stated_rate")} />
          </label>
          <input
            id="service-rate"
            data-testid="service-rate"
            inputMode="decimal"
            value={form.rate}
            onChange={(e) => set("rate")(e.target.value)}
          />
          <SheetWarning held={held("stated_rate")} />
        </>
      )}
      {fieldError && (
        <p role="alert" className="error" data-testid="service-error">
          {fieldError}
        </p>
      )}
      {saved && !saved.ok && !saved.field && (
        <p role="alert" className="error" data-testid="service-error">
          {saved.text}
          {saved.session && (
            <>
              {" "}
              <button type="button" onClick={() => signOut(auth)}>
                Sign in again
              </button>
            </>
          )}
        </p>
      )}
      <div className="actions">
        <button className="primary" data-testid="save-service" disabled={saving || !Array.isArray(areas)}>
          {saving ? "Saving…" : "Save the changes"}
        </button>
        <button type="button" data-testid="cancel-service" disabled={saving} onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}

type Load = api.AccountDetail | "missing" | "server" | "network" | "session" | undefined;

export function AccountPage({
  id,
  base,
  org,
  auth,
  user,
  listAllowed = true,
  canVisits = false,
  canSensors = false,
}: {
  id: string;
  base: string;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  listAllowed?: boolean;
  canVisits?: boolean;
  canSensors?: boolean;
}) {
  const [account, setAccount] = useState<Load>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [correcting, setCorrecting] = useState<api.Pickup | null>(null);
  const [editing, setEditing] = useState(false);
  const [serviceNote, setServiceNote] = useState<"saved" | "undone" | null>(null);
  const [undo, setUndo] = useState<api.AccountChange | null>(null);
  const [undoError, setUndoError] = useState<string | null>(null);
  const [undoing, setUndoing] = useState(false);
  const [issueFromCall, setIssueFromCall] = useState<string | null>(null);
  const [askRequest, setAskRequest] = useState(0);
  const [issueNote, setIssueNote] = useState("");
  const [allCalls, setAllCalls] = useState(false);
  const [callSort, setCallSort] = useSort("calls", CALL_COLUMNS);
  const [pickupSort, setPickupSort] = useSort("pickups", PICKUP_COLUMNS);
  const [reviewSort, setReviewSort] = useSort("reviews", REVIEW_RECORD_COLUMNS);
  const issueStatus = useRef<HTMLParagraphElement>(null);
  const orderStatus = useRef<HTMLParagraphElement>(null);
  const [orderNote, setOrderNote] = useState("");
  const [orderFromPhoto, setOrderFromPhoto] = useState<{ text: string; key: number } | null>(null);
  const [loadingAll, setLoadingAll] = useState(false);
  const editButton = useRef<HTMLButtonElement>(null);
  useTitle(account && typeof account === "object" ? `${account.name} · ${org.name}` : `Account · ${org.name}`);
  useCrumb(account && typeof account === "object" ? account.number : null);

  useEffect(() => {
    if (!isId(id)) {
      setAccount("missing");
      return;
    }
    setAccount(undefined);
    user
      .getIdToken()
      .then((token) => api.account(token, id))
      .then(setAccount)
      .catch((e) => {
        const code = e instanceof api.ApiError ? e.status : 0;
        setAccount(code === 404 ? "missing" : code === 401 ? "session" : code >= 500 ? "server" : "network");
      });
  }, [id, user, attempt]);

  const loaded = account !== undefined;
  useEffect(() => {
    if (loaded && document.activeElement === document.body) document.querySelector<HTMLElement>("main h1")?.focus();
  }, [loaded]);

  const fromRoute = new URLSearchParams(location.search).get("from") === "route";
  const back = (
    <p>
      {fromRoute ? (
        <a href={`${base}/today`} onClick={go} className="back" data-testid="back-to-route">
          ← Back to the route
        </a>
      ) : (
        <a href={listAllowed ? `${base}/accounts` : base} onClick={go} className="back" data-testid="back-to-accounts">
          {listAllowed ? "← All accounts" : "← Home"}
        </a>
      )}
    </p>
  );
  if (account === undefined) {
    return (
      <main>
        {back}
        <Skeleton shape="detail" />
      </main>
    );
  }
  if (account === "missing") {
    return (
      <main data-testid="account-missing">
        {back}
        <h1 tabIndex={-1}>Account not found</h1>
        <p>No account at this link is visible to you.</p>
      </main>
    );
  }
  if (account === "session") {
    return (
      <main data-testid="session-ended">
        {back}
        <h1 tabIndex={-1}>Your session ended</h1>
        <button className="primary" onClick={() => signOut(auth)}>
          Sign in again
        </button>
      </main>
    );
  }
  if (account === "server" || account === "network") {
    return (
      <main data-testid="account-failed">
        {back}
        <h1 tabIndex={-1}>Cannot load the account</h1>
        <p role="alert" className="error">
          {account === "server" ? SERVER_PROBLEM : "Cannot reach the server. Check your connection."}{" "}
          <button onClick={() => setAttempt((n) => n + 1)}>Try again</button>
        </p>
      </main>
    );
  }
  const plan = account.plan;
  const ended = account.status === "OFF";
  const estimate = ended ? undefined : account.projection?.estimate;
  const rows = pickupRows(account.pickups);
  const showAll = async () => {
    setLoadingAll(true);
    try {
      setAccount(await api.account(await user.getIdToken(), account.id, true));
    } catch {
      setLoadingAll(false);
      return;
    }
    setLoadingAll(false);
  };
  const undoLast = async () => {
    if (!undo) return;
    setUndoing(true);
    setUndoError(null);
    try {
      setAccount(await api.changeAccount(await user.getIdToken(), account.id, undo));
      setUndo(null);
      setServiceNote("undone");
    } catch (e) {
      setUndoError(saveFailure(e, "The change").text);
    } finally {
      setUndoing(false);
    }
  };
  const openIssues = (account.issues ?? []).filter((found) => found.status !== "closed").length;
  const sensorTanks = [...new Set(account.containers.flatMap((container) => (container.tank_id ? [container.tank_id] : [])))];
  return (
    <main className="account-page wide" data-testid="account-page" data-today={todayIn(org.time_zone)}>
      {back}
      <h1 tabIndex={-1}>
        <span className="account-number">{account.number}</span> {account.name} <SourceTag tag={account.data_source} />
      </h1>
      <DeletedBanner kind="account" deleted={account.deleted} timeZone={org.time_zone} />
      {account.address && <p className="address">{account.address}</p>}
      {(account.can_log_call || account.can_request_pickup || openIssues > 0) && (
        <p className="jump-links">
          {openIssues > 0 && (
            <a
              href="#account-issues"
              data-testid="jump-to-issues"
              onClick={(event) => {
                event.preventDefault();
                const title = document.getElementById("account-issues");
                title?.scrollIntoView({ block: "start" });
                title?.focus({ preventScroll: true });
              }}
            >
              {openIssues === 1 ? "1 open issue" : `${openIssues} open issues`}
            </a>
          )}
          {account.can_log_call && (
          <a
            href="#call-form-title"
            data-testid="jump-to-call"
            onClick={(event) => {
              event.preventDefault();
              document.getElementById("call-form-title")?.scrollIntoView({ block: "start" });
              document.getElementById("call-reason")?.focus({ preventScroll: true });
            }}
          >
            Log a call
          </a>
          )}
          {account.can_request_pickup && (
            <a
              href="#account-requests"
              data-testid="jump-to-request"
              onClick={(event) => {
                event.preventDefault();
                setAskRequest((n) => n + 1);
              }}
            >
              Request a pickup
            </a>
          )}
        </p>
      )}
      {account.can_record && (
        <PickupForm
          account={account}
          org={org}
          auth={auth}
          user={user}
          correcting={correcting}
          quiet={editing}
          onCancel={() => setCorrecting(null)}
          onSaved={(next) => {
            setAccount(next);
            setCorrecting(null);
          }}
        />
      )}
      <section aria-labelledby="account-service">
        <h2 id="account-service">Service</h2>
        {Boolean(account.from_sheet?.length) && (
          <p className="sub" data-testid="account-from-sheet">
            From the sheet: the service sheet holds the name, the address, the status, the plan and the capacity of this account. The import
            writes the sheet values again when the sheet changes, and each night.
          </p>
        )}
        <dl className="facts">
          <dt>Status</dt>
          <dd data-testid="account-status">
            {STATUS_TEXT[account.status]}
            <SheetNote held={sheetHeld(account.from_sheet, "status")} />
          </dd>
          <CommercialFact account={account} />
          {plan ? (
            <>
              <dt>Area</dt>
              <dd>
                {plan.area} · rotation week {plan.rotation_week}
                {account.outside_area && (
                  <Hint text={OUTSIDE_HELP}>
                    <span className="below outside-area-warning" data-testid="account-outside-area">
                      <span aria-hidden="true">⚠ </span>
                      {outsideText(account.outside_area)}
                    </span>
                  </Hint>
                )}
              </dd>
              <dt>Visits</dt>
              <dd>{weekGroupText(account) || `Week ${plan.week_group}`}</dd>
              <dt>Service window</dt>
              <dd>{windowText(account)}</dd>
              <dt>Blackout windows</dt>
              <dd data-testid="account-blackouts">{blackoutText(plan.blackouts)}</dd>
              <dt>Season</dt>
              <dd data-testid="account-season">
                {seasonText(plan.season)}
                {account.projection?.off_season && (
                  <>
                    {" "}
                    <span className="chip-flag" data-testid="off-season">
                      {offSeasonText(account.projection)}
                    </span>
                  </>
                )}
              </dd>
              <dt>Stated rate</dt>
              <dd>{galText(plan.stated_rate)} each week</dd>
            </>
          ) : (
            <>
              <dt>Service plan</dt>
              <dd>None</dd>
            </>
          )}
          <dt>Capacity</dt>
          <dd>{galText(account.capacity_gal)}</dd>
          <dt>Recommended size</dt>
          <dd data-testid="account-recommended">
            {account.plan === null ? (
              "No estimate. Add a service plan."
            ) : (
              <>
                {recommendedText(account.projection)} <span className="chip-flag">Estimated</span>
              </>
            )}
          </dd>
        </dl>
        {account.can_edit && editing && (
          <ServiceForm
            account={account}
            auth={auth}
            user={user}
            onClose={() => {
              setEditing(false);
              setTimeout(() => editButton.current?.focus());
            }}
            onSaved={(next, previous) => {
              setAccount(next);
              setEditing(false);
              setServiceNote("saved");
              setUndo(previous);
              setUndoError(null);
              setTimeout(() => editButton.current?.focus());
            }}
          />
        )}
        {account.can_edit && !editing && (
          <p className="actions">
            <button
              ref={editButton}
              data-testid="edit-service"
              onClick={() => {
                setEditing(true);
                setServiceNote(null);
                setUndo(null);
                setUndoError(null);
              }}
            >
              Change the service
            </button>
          </p>
        )}
        <p role="status" data-testid="service-status-text">
          {serviceNote === "saved" && "Saved. The service has the new values."}
          {serviceNote === "undone" && "Undone."}
          {serviceNote === "saved" && undo && (
            <>
              {" "}
              <button type="button" data-testid="undo-service" disabled={undoing} onClick={undoLast}>
                {undoing ? "Undoing…" : "Undo"}
              </button>
            </>
          )}
        </p>
        {undoError && (
          <p role="alert" className="error" data-testid="undo-error">
            {undoError}
          </p>
        )}
      </section>
      <LocationCard
        accountId={account.id}
        door={account.door}
        tank={account.tank}
        hasTank
        canDoor={Boolean(account.can_set_door)}
        canTank={Boolean(account.can_set_tank)}
        address={account.address ?? ""}
        timeZone={org.time_zone}
        user={user}
        save={async (change) => setAccount(await api.setAccountLocation(await user.getIdToken(), account.id, change))}
        fill={ended ? null : (estimate?.today.p50 ?? account.projection?.load_share ?? null)}
        fillName={`${account.number} ${account.name}`}
      />
      <PortalInvite account={account} user={user} />
      {account.projection && (
        <section aria-labelledby="account-estimate">
          <h2 id="account-estimate">Estimate</h2>
          {estimate && !account.projection.off_season && <AccountConeChart accountId={account.id} />}
          <dl className="facts" data-testid="account-estimate">
            {estimate ? (
              <EstimateFacts estimate={estimate} containers={account.containers} />
            ) : (
              <>
                <dt>Estimated load</dt>
                <dd data-testid="account-load">{ended ? `${STATUS_TEXT.OFF} account, so no estimate` : account.capacity_gal > 0 ? loadText(account) : "No container, so no estimate"}</dd>
                <dt>Weeks left</dt>
                <dd>{weeksLeftText(account)}</dd>
              </>
            )}
            <dt>Next visit</dt>
            <dd data-testid="account-next-visit" data-day={account.projection.next_visit ?? ""}>
              {nextVisitText(account)}
              {!estimate && !ended && account.capacity_gal > 0 && nextVisitText(account) !== "–" && (
                <span className="sub">{projectedText(account)} (estimated)</span>
              )}
            </dd>
            <dt>Last pickup</dt>
            <dd>{newestText(account)}</dd>
            <dt>Estimated rate</dt>
            <dd>{rateText(account)}</dd>
            <dt>Container weeks</dt>
            <dd>{capWeeksText(account)}</dd>
            <dt>Estimated scrap</dt>
            <dd>{scrapText(account)}</dd>
            {plan?.forecast_from && (
              <>
                <dt>Forecast from</dt>
                <dd data-testid="forecast-from">{forecastFromText(plan.forecast_from, plan.forecast_reason)}</dd>
              </>
            )}
            <dt>Flags</dt>
            <dd className="flags">
              {flagsOf(account).length || earlyPullOf(account) ? <Flags account={account} /> : "None"}
              {account.reviews &&
                flagsOf(account)
                  .filter((flag): flag is api.Flag & ReviewKind => (REVIEW_KINDS as readonly string[]).includes(flag))
                  .map((flag) => {
                    const text = reviewedText(flag, account.reviews ?? [], todayIn(org.time_zone));
                    return (
                      text && (
                        <span key={flag} className="sub flag-reviewed" data-testid="flag-reviewed">
                          {flagText(account, flag)}: {text}
                        </span>
                      )
                    );
                  })}
            </dd>
          </dl>
          {!ended && (
            <>
              <p className="footnote">
                {estimate
                  ? "The estimate comes from the pickups and the sensor reports. It is not a measurement."
                  : "The estimate uses the same rules as the service sheet. It is not a measurement."}
              </p>
              <ForecastReset account={account} user={user} today={todayIn(org.time_zone)} onSaved={setAccount} />
            </>
          )}
        </section>
      )}
      <section aria-labelledby="account-containers">
        <h2 id="account-containers">Containers</h2>
        {account.containers.length ? (
          <ul className="containers" data-testid="account-containers">
            {account.containers.map((container) => (
              <li key={container.id}>
                {galText(container.size_gal)} {container.kind} ·{" "}
                <ContainerSensor
                  accountId={account.id}
                  container={container}
                  canEdit={account.can_order && !account.deleted}
                  user={user}
                  onSaved={setAccount}
                />
              </li>
            ))}
          </ul>
        ) : (
          <p>No containers.</p>
        )}
        {account.units && account.units.length > 0 && (
          <p data-testid="account-units">
            Serial numbers here: {account.units.map((unit) => `${unit.serial} (${galText(unit.size_gal)})`).join(", ")}
          </p>
        )}
      </section>
      {canSensors && sensorTanks.length > 0 && <SensorSection tankIds={sensorTanks} auth={auth} user={user} timeZone={org.time_zone} />}
      {account.can_view_photos && (
        <AccountPhotos
          account={account}
          org={org}
          auth={auth}
          user={user}
          focus={new URLSearchParams(location.search).get("photo")}
          onOrder={account.can_order ? (text) => setOrderFromPhoto({ text, key: Date.now() }) : null}
        />
      )}
      <AccountSalesSection accountId={account.id} user={user} timeZone={org.time_zone} />
      {account.contract && <ContractSection account={account} org={org} auth={auth} user={user} onSaved={setAccount} />}
      <PayoutHistory account={account} base={base} />
      <AccountRequests account={account} org={org} auth={auth} user={user} ask={askRequest} onSaved={setAccount} />
      {(account.calls || account.can_log_call) && (
        <section aria-labelledby="account-calls" data-testid="call-section">
          <h2 id="account-calls">Calls</h2>
          {account.can_log_call && (
            <CallForm
              account={account}
              auth={auth}
              user={user}
              quiet={account.can_record || editing}
              onSaved={setAccount}
              leadBase={account.can_make_lead ? base : undefined}
              onOpenIssue={
                account.can_open_issue
                  ? (callText) => {
                      setIssueFromCall(callText);
                      setTimeout(() => document.getElementById("issue-reason")?.focus());
                    }
                  : undefined
              }
            />
          )}
          {account.calls === null ? null : account.calls.length ? (
            <>
              <SortSelect id="calls-select" columns={CALL_COLUMNS} sort={callSort} onSort={setCallSort} fallback="Newest first" />
              <div className="table-scroll" tabIndex={0} role="region" aria-label="Calls">
                <table className="list calls-table" data-testid="call-table" data-cols="calls-select">
                  <thead>
                    <tr>
                      {CALL_COLUMNS.map((column) => (
                        <SortTh key={column.key} column={column} sort={callSort} onSort={setCallSort} />
                      ))}
                      <th scope="col">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortRows(allCalls ? account.calls : account.calls.slice(0, CALLS_FIRST), CALL_COLUMNS, callSort).map((found) => (
                      <tr key={found.id} data-testid="call-row" data-day={callDay(found.at, org.time_zone)}>
                        <th scope="row">{callTime(found.at, org.time_zone)}</th>
                        <td data-label="Reason">{REASON_TEXT[found.reason]}</td>
                        <td data-label="Caller">{found.caller}</td>
                        <td data-label="At fault">
                          {found.at_fault ? (
                            <span className="chip-flag serious" data-testid="call-at-fault-mark">
                              At fault
                            </span>
                          ) : (
                            "No"
                          )}
                        </td>
                        <td data-label="Result">{found.result || "–"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <p data-testid="account-no-calls">No calls yet.</p>
          )}
          {!allCalls && account.calls && account.calls.length > CALLS_FIRST && (
            <p className="actions">
              <button type="button" data-testid="show-all-calls" onClick={() => setAllCalls(true)}>
                Show all {account.calls.length} calls
              </button>
            </p>
          )}
          {allCalls && account.calls && account.calls.length >= CALLS_SHOWN && (
            <p className="sub" data-testid="calls-limit">
              The {CALLS_SHOWN} newest calls show.
            </p>
          )}
        </section>
      )}
      {(account.issues || account.can_open_issue) && (
      <section aria-labelledby="account-issues" data-testid="issue-section">
        <h2 id="account-issues" tabIndex={-1}>Issues</h2>
        {account.can_open_issue && (
          <IssueForm
            account={account}
            org={org}
            auth={auth}
            user={user}
            quiet={account.can_record || account.can_log_call || editing}
            fromCall={issueFromCall}
            onSaved={(next) => {
              setAccount(next);
              setIssueFromCall(null);
            }}
          />
        )}
        <p ref={issueStatus} tabIndex={-1} role="status" data-testid="issue-step-status">
          {issueNote}
        </p>
        {account.issues === null ? null : account.issues.length ? (
          <IssueTable
            issues={account.issues}
            base={null}
            org={org}
            auth={auth}
            user={user}
            param="issues"
            fallback="Most urgent first"
            onChanged={async (_issue, text) => {
              setIssueNote(text);
              requestAnimationFrame(() => issueStatus.current?.focus());
              try {
                setAccount(await api.account(await user.getIdToken(), account.id));
              } catch {
                setIssueNote(`${text} Reload the page to see the issue.`);
              }
            }}
          />
        ) : (
          <p data-testid="account-no-issues">No issues.</p>
        )}
      </section>
      )}
      {canVisits && <AccountVisits id={account.id} org={org} user={user} />}
      <section aria-labelledby="account-pickups">
        <h2 id="account-pickups">Pickups</h2>
        {rows.length > 0 && (
          <SortSelect id="pickups-select" columns={PICKUP_COLUMNS} sort={pickupSort} onSort={setPickupSort} fallback="Newest first" />
        )}
        {rows.length ? (
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Pickups">
            <table className="pickups list" data-testid="account-pickups" data-cols="pickups-select">
              <thead>
                <tr>
                  {PICKUP_COLUMNS.map((column) => (
                    <SortTh key={column.key} column={column} sort={pickupSort} onSort={setPickupSort} />
                  ))}
                  {account.can_record && (
                    <th scope="col">
                      <span className="visually-hidden">Correct</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {sortRows(rows, PICKUP_COLUMNS, pickupSort).map(({ pickup, replacedBy, replaces }) => (
                  <tr
                    key={pickup.id}
                    className={replacedBy || pickup.replaced ? "row-corrected" : undefined}
                    data-testid="pickup-row"
                    data-at={pickup.at}
                    data-gal={`${pickup.raw_gal}/${pickup.scrap_gal}`}
                  >
                    <th scope="row">
                      {dayText(pickup.at, true)}
                      {replacedBy && <span className="sub">Replaced by a correction</span>}
                      {pickup.replaced && (
                        <span className="sub" data-testid="pickup-replaced">
                          {pickup.on_app ? "Sheet, area on the app" : "Sheet, replaced by app"}
                        </span>
                      )}
                      {replaces && <span className="sub">Correction of the {dayText(replaces.at)} pickup</span>}
                      {pickup.load && (
                        <span className="sub" data-testid="pickup-load">
                          {pickupLoadText(pickup.load, org.time_zone)}
                        </span>
                      )}
                    </th>
                    <td className="num" data-label="Raw">{galText(pickup.raw_gal)}</td>
                    <td className="num" data-label="Scrap">{galText(pickup.scrap_gal)}</td>
                    <td className="num" data-label="Net">{galText(netGal(pickup))}</td>
                    {account.can_record && (
                      <td data-label="">
                        {!replacedBy && lockedBy(pickup.load) && (
                          <span className="sub" data-testid="pickup-locked">
                            {lockedBy(pickup.load)}
                          </span>
                        )}
                        {!replacedBy && !lockedBy(pickup.load) && (
                          <button
                            type="button"
                            className="link"
                            data-testid="correct-pickup"
                            aria-label={`Correct the pickup of ${dayText(pickup.at, true)}`}
                            onClick={() => setCorrecting(pickup)}
                          >
                            Correct
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p data-testid="account-no-pickups">No pickups yet.</p>
        )}
        {account.pickups_total > account.pickups.length && (
          <p className="actions">
            <button type="button" data-testid="show-all-pickups" disabled={loadingAll} onClick={showAll}>
              {loadingAll ? "Loading…" : `Show all ${account.pickups_total} pickups`}
            </button>
          </p>
        )}
      </section>
      {(account.work_orders || account.can_order) && (
      <section aria-labelledby="account-orders" data-testid="order-section">
        <h2 id="account-orders">Work orders</h2>
        {account.can_order && (
          <WorkOrderForm
            account={account}
            auth={auth}
            user={user}
            quiet={account.can_record || account.can_log_call || account.can_open_issue || editing}
            onSaved={setAccount}
            startNote={orderFromPhoto}
          />
        )}
        <p ref={orderStatus} tabIndex={-1} role="status" data-testid="order-step-status">
          {orderNote}
        </p>
        {account.work_orders === null ? null : account.work_orders.length ? (
          <WorkOrderTable
            orders={account.work_orders}
            base={null}
            org={org}
            auth={auth}
            user={user}
            param="orders"
            onChanged={async (text) => {
              setOrderNote(text);
              try {
                setAccount(await api.account(await user.getIdToken(), account.id));
              } catch {
                setOrderNote(`${text} Reload the page to see the work order.`);
              }
              requestAnimationFrame(() => orderStatus.current?.focus());
            }}
          />
        ) : (
          <p data-testid="account-no-orders">No work orders.</p>
        )}
      </section>
      )}
      {account.reviews && (
        <section aria-labelledby="account-reviews" data-testid="review-section">
          <h2 id="account-reviews">Review decisions</h2>
          {account.reviews.length ? (
            <>
              <SortSelect id="reviews-select" columns={REVIEW_RECORD_COLUMNS} sort={reviewSort} onSort={setReviewSort} fallback="Newest first" />
              <table className="list" data-testid="review-record-table" data-cols="reviews-select">
                <thead>
                  <tr>
                    {REVIEW_RECORD_COLUMNS.map((column) => (
                      <SortTh key={column.key} column={column} sort={reviewSort} onSort={setReviewSort} />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortRows(account.reviews, REVIEW_RECORD_COLUMNS, reviewSort).map((record) => (
                    <tr key={record.id} data-testid="review-record">
                      <th scope="row">{(record.decided_at ?? "").slice(0, 10)}</th>
                      <td data-label="Kind">{KIND_SHORT[record.kind]}</td>
                      <td data-label="Result">{RESULT_TEXT[record.result]}</td>
                      <td data-label="Reason">{record.note || "–"}</td>
                      <td data-label="By">{record.decided_by ? personName(record.decided_by) : "–"}</td>
                      <td data-label="Until">{record.until ?? "a newer overflow call"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <p data-testid="account-no-reviews">No review decisions.</p>
          )}
        </section>
      )}
      <DeleteRecord
        kind="account"
        names={[account.name, account.number]}
        deleted={account.deleted}
        canDelete={Boolean(account.can_delete)}
        canPurge={Boolean(account.can_purge)}
        onDelete={async (reason) => {
          const token = await user.getIdToken();
          await api.deleteAccount(token, account.id, reason);
          setAccount(await api.account(token, account.id));
        }}
        onRestore={async () => {
          const token = await user.getIdToken();
          await api.restoreAccount(token, account.id);
          setAccount(await api.account(token, account.id));
        }}
        onPurge={async (confirm) => {
          await api.purgeAccount(await user.getIdToken(), account.id, confirm);
          navigate(listAllowed ? `${base}/accounts` : base);
        }}
      />
    </main>
  );
}


/** Roadmap 063: the fill band today and at the next visit, from the pickups and the sensor. */
function EstimateFacts({ estimate, containers }: { estimate: api.FillEstimate; containers: api.Container[] }) {
  const lag = sensorLagText(estimate, containers);
  return (
    <>
      <dt>Fill today</dt>
      <dd data-testid="account-fill-today">
        {bandText(estimate.today)} (estimated)
        <span className="sub">
          {sensorText(estimate.sensor, estimate.float_pct)} · {anchorText(estimate.anchor)}
        </span>
        {lag && (
          <span className="sub" data-testid="account-sensor-lag">
            {lag}
          </span>
        )}
      </dd>
      {estimate.at_visit && (
        <>
          <dt>At the next visit</dt>
          <dd data-testid="account-fill-visit">
            {bandText(estimate.at_visit)} (estimated), {chanceText(estimate.at_visit.risk)}
          </dd>
        </>
      )}
      {estimate.season && (
        <>
          <dt>Season</dt>
          <dd data-testid="account-fill-season">{swingText(estimate.season)}</dd>
        </>
      )}
    </>
  );
}
